import * as React from 'react'

import { DefaultContext, QueryResult, useQuery } from '@apollo/client'
import deepmerge from 'deepmerge'
import type { DataTableSource } from 'generated/resources/datatable.graphql'
import once from 'lodash/once'

import useFilters from 'modules/core/components/DataTable/hooks/useFilters'
import useSort from 'modules/core/components/DataTable/hooks/useSort'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import { PaginationStateContext } from './PaginationStateContext'

type TData = any
type TVariables = any

type Context<T, S extends string> = {
  rowTo?: (args: Pick<T, keyof T>) => string | undefined
  queryResult: QueryResult<TData, TVariables>
  loading: boolean
  variables: TVariables
  source: DataTableSource<T, S>
  data: Array<T>
  queryContext?: DefaultContext
}

type Props<T, S extends string> = {
  children: React.ReactNode
  source: DataTableSource<T, S>
  rowTo?: (args: Pick<T, keyof T>) => string | undefined
  queryContext?: DefaultContext
}

export const createDataContext = once(<T, S extends string>() =>
  React.createContext<Context<T, S> | null>(null)
)

function getResolvedData<T, S extends string>(
  queryResult: QueryResult<TData, TVariables>,
  source: DataTableSource<T, S>
) {
  if (!queryResult.data) {
    return []
  }

  try {
    return source.resolver.edges(queryResult.data).map((mainEl: (string | React.ReactNode)[]) => {
      return Object.keys(source.resolver.fields).reduce(
        (acc, path) => {
          return {
            ...acc,
            [path]: source.resolver.fields[path as keyof T](mainEl),
          }
        },
        {} as Pick<T, keyof T>
      )
    })
  } catch (_error) {
    return []
  }
}

export function DataProvider<T, S extends string>({
  children,
  source,
  rowTo,
  queryContext,
}: Props<T, S>) {
  const Context = createDataContext<T, S>()
  const paginationState = React.useContext(PaginationStateContext)

  if (!paginationState) {
    throw new Error('PaginationStateContext is not defined')
  }

  const { query, resolver, variables: extraVariables } = source

  const { getSortVariables } = useSort()
  const { getFiltersVariables } = useFilters()

  useEffect(() => {
    paginationState.resetPagination()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(extraVariables)])

  const variables = deepmerge(
    {
      ...paginationState.getPaginationVariables(),
      ...getSortVariables(),
      ...getFiltersVariables(),
    },
    extraVariables
  )

  const queryResult = useQuery(query, {
    variables,
    context: queryContext,
    onCompleted() {
      setCustomLoading(false)
    },
  })

  const data = useUnsafeMemo(() => {
    return getResolvedData<T, S>(queryResult, source)
  }, [queryResult.data, resolver])

  const [customLoading, setCustomLoading] = React.useState(data.length === 0)

  const value = {
    data,
    rowTo,
    queryResult,
    loading: customLoading,
    variables,
    source,
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}
